// @ts-check
// Moldura do painel de suporte no layout do CRM: menu lateral com as seções (Clientes, Financeiro, Plataforma),
// rodapé com quem está logado e a versão, e a área da página à direita. No celular o menu vira uma barra no topo e a
// gaveta abre por cima com o mesmo `data-mobile-open` da gaveta do CRM (shell.css), porque a gaveta do CRM depende do
// estado da escola logada e o painel não tem escola. O tema (Sistema, Claro, Escuro) é o mesmo do CRM, guardado no
// navegador; o painel só não tinha onde escolher, então o rodapé ganhou os três botões.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { Link, navigate } from '../../../app/router.js';
import { BrandMark } from '../../../ui/brand-logo.js';
import { Icon } from '../../../ui/icon.js';
import { LightIconButton } from '../../../ui/button.js';
import { APP_VERSION } from '../../../../../shared/app-version.js';
import { ADMIN_NAVIGATION, adminPath } from '../../../lib/admin-navigation.js';
import { t } from '../../../i18n/index.js';
import { COLOR_SCHEMES, COLOR_SCHEME_ICONS, colorSchemeState, setColorScheme } from '../../../app/theme.js';

/**
 * @param {{ email: string, section: import('../../../lib/admin-navigation.js').AdminSectionKey, onSignOut: () => void, children: any }} props
 */
export function AdminShell({ email, section, onSignOut, children }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const closeMenu = () => setIsMenuOpen(false);
  return html`<div class="admin-layout" data-menu-open=${isMenuOpen ? 'true' : undefined}>
    <header class="admin-mobile-bar">
      <${LightIconButton} icon=${isMenuOpen ? 'IconX' : 'IconMenu2'} accent="secondary" size="medium" ariaLabel=${t('navigation.openMenu')} onClick=${() => setIsMenuOpen(!isMenuOpen)} />
      <span class="admin-mobile-title"><${BrandMark} size=${20} />${t('operations.admin.title')}</span>
    </header>
    <div class="admin-container">
      <aside class="navigation-drawer admin-drawer" data-expanded="true" data-mobile-open=${isMenuOpen ? 'true' : undefined} aria-label=${t('operations.admin.title')}>
        <div class="navigation-drawer-inner">
          <div class="navigation-drawer-header admin-drawer-header">
            <${Link} href=${adminPath('overview')} className="admin-brand" onClick=${closeMenu}>
              <${BrandMark} size=${22} />
              <span class="admin-brand-titles"><span class="admin-brand-name">${t('operations.admin.title')}</span><span class="admin-brand-hint">${t('operations.admin.panel.brandHint')}</span></span>
            </${Link}>
          </div>
          <nav class="navigation-drawer-scroll admin-drawer-scroll">
            ${ADMIN_NAVIGATION.map((group) => html`<div key=${group.key} class="navigation-section">
              <div class="navigation-section-title navigation-section-title-static"><span>${t(`operations.admin.panel.groups.${group.key}`)}</span></div>
              <div class="navigation-section-items">
                ${group.items.map((item) => html`<${Link} key=${item.key} href=${adminPath(item.key)} className="navigation-item" data-active=${item.key === section ? 'true' : undefined} onClick=${closeMenu}>
                  <span class="navigation-item-icon"><${Icon} name=${item.icon} size=${16} /></span>
                  <span class="navigation-item-label">${t(`operations.admin.panel.sections.${item.key}`)}</span>
                </${Link}>`)}
              </div>
            </div>`)}
          </nav>
          <div class="admin-drawer-footer">
            <div class="admin-drawer-theme" role="group" aria-label=${t('navigation.theme')}>
              <span class="admin-drawer-theme-label">${t('navigation.theme')}</span>
              ${COLOR_SCHEMES.map((scheme) => html`<${LightIconButton}
                key=${scheme}
                icon=${COLOR_SCHEME_ICONS[scheme]}
                accent="secondary"
                active=${colorSchemeState.value === scheme}
                ariaLabel=${t(`colorSchemes.${scheme}`)}
                title=${t(`colorSchemes.${scheme}`)}
                aria-pressed=${colorSchemeState.value === scheme ? 'true' : 'false'}
                onClick=${() => setColorScheme(scheme)}
              />`)}
            </div>
            <div class="admin-drawer-account" title=${email}>
              <span class="admin-drawer-avatar"><${Icon} name="IconHeadset" size=${14} /></span>
              <span class="admin-drawer-email">${email}</span>
              <${LightIconButton} icon="IconLogout" accent="secondary" ariaLabel=${t('operations.admin.signOut')} onClick=${onSignOut} />
            </div>
            <button type="button" class="admin-drawer-version" onClick=${() => navigate('/release-notes')}>${t('navigation.versionNumber', { version: APP_VERSION })}</button>
          </div>
        </div>
      </aside>
      ${isMenuOpen ? html`<div class="admin-menu-backdrop" onClick=${closeMenu}></div>` : null}
      <main class="main-container admin-main-container">${children}</main>
    </div>
  </div>`;
}
